import React, { useMemo, useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { Button, Card, Col, Form, Modal, OverlayTrigger, Row, Spinner, Table, Tooltip } from 'react-bootstrap';
import { LevelRewardInputType } from '../../../../../__gqltypes__';
import { typeEmoji } from '../../../liveops/components/formatReward';
import { DELETE_UPDATE_GIFT_HISTORY, GET_UPDATE_GIFT_HISTORY, SET_LATEST_UPDATE_GIFT, UPDATE_UPDATE_GIFT_HISTORY } from './graphql';
import { normalizeVersion } from '../VersionControl/utils';

type LatestUpdateGiftFormState = {
  eligibleIosVersionsText: string;
  eligibleAndroidVersionsText: string;
  gift1Type: string;
  gift1Amount: string;
  gift2Type: string;
  gift2Amount: string;
  betaVersionsIos: Record<string, boolean>;
  betaVersionsAndroid: Record<string, boolean>;
};

type UpdateGiftHistoryRow = {
  id: string;
  appId: string;
  eligibleIosVersions: number[];
  eligibleAndroidVersions: number[];
  betaIosVersions?: number[] | null;
  betaAndroidVersions?: number[] | null;
  gift1Type: string;
  gift1Amount: number;
  gift2Type: string;
  gift2Amount: number;
  createdAt: number | string;
};

type RewardPresetKey = 'major' | 'minor' | 'normal';

const UpdateGiftHistory = () => {
  const rewardPresets: Record<RewardPresetKey, { label: string; coin: string; diamond: string }> = {
    major: { label: 'Major', coin: '300', diamond: '30' },
    minor: { label: 'Minor', coin: '50', diamond: '10' },
    normal: { label: 'Normal', coin: '100', diamond: '20' },
  };
  const [latestPreset, setLatestPreset] = useState<RewardPresetKey>('major');
  const [editPreset, setEditPreset] = useState<RewardPresetKey>('major');
  const [latestGift, setLatestGift] = useState<LatestUpdateGiftFormState>({
    eligibleIosVersionsText: '',
    eligibleAndroidVersionsText: '',
    gift1Type: LevelRewardInputType.CURRENCY_COIN,
    gift1Amount: '300',
    gift2Type: LevelRewardInputType.CURRENCY_DIAMOND,
    gift2Amount: '30',
    betaVersionsIos: {},
    betaVersionsAndroid: {},
  });
  const currencyRewards = useMemo(
    () => [LevelRewardInputType.CURRENCY_COIN, LevelRewardInputType.CURRENCY_DIAMOND],
    []
  );
  const formatGiftType = (type?: string) => {
    if (!type) {
      return 'N/A';
    }
    const emoji = typeEmoji[type as keyof typeof typeEmoji] ?? '';
    return `${emoji}${type}`.trim();
  };
  const stripBetaMarker = (value: string) => value.replace(/\(beta\)|-beta/gi, '').trim();
  const parseVersionEntries = (value?: string) => {
    if (!value) {
      return [] as Array<{ version: string; isBeta: boolean }>;
    }
    return value
      .split(/[\n,]+/)
      .map((item) => item.trim())
      .filter(Boolean)
      .map((item) => {
        const isBeta = /(\(beta\)|-beta)/i.test(item);
        const normalized = normalizeVersion(stripBetaMarker(item));
        return { version: normalized, isBeta };
      })
      .filter((item) => item.version);
  };
  const parseVersionList = (value?: string) => {
    if (!value) {
      return [] as string[];
    }
    const items = parseVersionEntries(value).map((item) => item.version);
    return Array.from(new Set(items));
  };
  const mergeBetaMapFromEntries = (
    prevMap: Record<string, boolean>,
    entries: Array<{ version: string; isBeta: boolean }>
  ) => {
    if (!entries.length) {
      return prevMap;
    }
    const nextMap = { ...prevMap };
    entries.forEach(({ version, isBeta }) => {
      if (version) {
        nextMap[version] = isBeta;
      }
    });
    return nextMap;
  };
  const toNumericVersionList = (value?: string) =>
    parseVersionList(value)
      .map((version) => Number(version.replace(/[^0-9]/g, '')))
      .filter((version) => Number.isFinite(version) && version > 0);
  const toNumericVersion = (value: string) => Number(value.replace(/[^0-9]/g, ''));
  const formatVersionNumber = (value: number) => {
    const digits = String(value ?? '').replace(/\D/g, '').padStart(9, '0');
    return digits.replace(/(\d{3})(\d{3})(\d{3})/, '$1.$2.$3');
  };
  const formatVersionList = (
    values?: number[] | null,
    isBeta?: boolean,
    betaMap?: Record<string, boolean>
  ) =>
    (values ?? [])
      .map((value) => formatVersionNumber(value))
      .map((version) => {
        if (betaMap && betaMap[version]) {
          return `${version} (beta)`;
        }
        return isBeta ? `${version} (beta)` : version;
      })
      .join(', ');
  const formatVersionListMultiline = (
    values?: number[] | null,
    isBeta?: boolean,
    betaMap?: Record<string, boolean>
  ) => formatVersionList(values, isBeta, betaMap).replace(/,\s*/g, '\n');
  const buildBetaMapFromNumbers = (values?: number[] | null) =>
    (values ?? []).reduce<Record<string, boolean>>((acc, value) => {
      acc[formatVersionNumber(value)] = true;
      return acc;
    }, {});
  const toNumericVersionListFromMap = (value?: string, betaMap?: Record<string, boolean>) =>
    parseVersionList(value)
      .filter((version) => Boolean(betaMap?.[version]))
      .map((version) => toNumericVersion(version))
      .filter((version) => Number.isFinite(version) && version > 0);

  const {
    data: historyData,
    loading: historyLoading,
    refetch: refetchHistory,
  } = useQuery(GET_UPDATE_GIFT_HISTORY, {
    variables: { limit: 50 },
    fetchPolicy: 'network-only',
  });
  const [setLatestUpdateGift, { loading: saveLoading }] = useMutation(SET_LATEST_UPDATE_GIFT);
  const [updateGiftHistory, { loading: editLoading }] = useMutation(UPDATE_UPDATE_GIFT_HISTORY);
  const [deleteGiftHistory, { loading: deleteLoading }] = useMutation(DELETE_UPDATE_GIFT_HISTORY);
  const [editRow, setEditRow] = useState<UpdateGiftHistoryRow | null>(null);
  const [sameVersion, setSameVersion] = useState(false);
  const [editState, setEditState] = useState({
    eligibleIosVersionsText: '',
    eligibleAndroidVersionsText: '',
    gift1Type: LevelRewardInputType.CURRENCY_COIN,
    gift1Amount: '300',
    gift2Type: LevelRewardInputType.CURRENCY_DIAMOND,
    gift2Amount: '30',
    betaVersionsIos: {} as Record<string, boolean>,
    betaVersionsAndroid: {} as Record<string, boolean>,
  });

  const handleLatestGiftChange = (updates: Partial<LatestUpdateGiftFormState>) => {
    setLatestGift((prev) => ({ ...prev, ...updates }));
  };
  const applyPresetToLatestGift = (preset: RewardPresetKey) => {
    const selected = rewardPresets[preset];
    setLatestPreset(preset);
    handleLatestGiftChange({
      gift1Type: LevelRewardInputType.CURRENCY_COIN,
      gift1Amount: selected.coin,
      gift2Type: LevelRewardInputType.CURRENCY_DIAMOND,
      gift2Amount: selected.diamond,
    });
  };
  const applyPresetToEditGift = (preset: RewardPresetKey) => {
    const selected = rewardPresets[preset];
    setEditPreset(preset);
    setEditState((prev) => ({
      ...prev,
      gift1Type: LevelRewardInputType.CURRENCY_COIN,
      gift1Amount: selected.coin,
      gift2Type: LevelRewardInputType.CURRENCY_DIAMOND,
      gift2Amount: selected.diamond,
    }));
  };

  const handleSave = () => {
    const eligibleIosVersions = toNumericVersionList(latestGift.eligibleIosVersionsText);
    const eligibleAndroidVersions = toNumericVersionList(latestGift.eligibleAndroidVersionsText);
    const betaIosVersions = toNumericVersionListFromMap(
      latestGift.eligibleIosVersionsText,
      latestGift.betaVersionsIos
    );
    const betaAndroidVersions = toNumericVersionListFromMap(
      latestGift.eligibleAndroidVersionsText,
      sameVersion ? latestGift.betaVersionsIos : latestGift.betaVersionsAndroid
    );
    const betaIosPayload = betaIosVersions.length ? betaIosVersions : null;
    const betaAndroidPayload = betaAndroidVersions.length ? betaAndroidVersions : null;
    if (!eligibleIosVersions.length && !eligibleAndroidVersions.length) {
      alert('Please, enter at least one eligible version');
      return;
    }
    setLatestUpdateGift({
      variables: {
        eligibleIosVersions,
        eligibleAndroidVersions,
        betaIosVersions: betaIosPayload,
        betaAndroidVersions: betaAndroidPayload,
        gift1Type: latestGift.gift1Type,
        gift1Amount: Number(latestGift.gift1Amount || 0),
        gift2Type: latestGift.gift2Type,
        gift2Amount: Number(latestGift.gift2Amount || 0),
      },
    })
      .then(() => {
        return Promise.all([refetchHistory()]);
      })
      .catch(() => {
        alert('Something went wrong :(');
      });
  };

  const historyRows = [...((historyData?.updateGiftHistory ?? []) as UpdateGiftHistoryRow[])].sort(
    (a, b) => Number(b.createdAt) - Number(a.createdAt)
  );
  const handleEditOpen = (row: UpdateGiftHistoryRow) => {
    setEditRow(row);
    setEditPreset('major');
    setEditState({
      eligibleIosVersionsText: formatVersionList(
        row.eligibleIosVersions,
        false,
        buildBetaMapFromNumbers(row.betaIosVersions)
      ),
      eligibleAndroidVersionsText: formatVersionList(
        row.eligibleAndroidVersions,
        false,
        buildBetaMapFromNumbers(row.betaAndroidVersions)
      ),
      gift1Type: (row.gift1Type as LevelRewardInputType) || LevelRewardInputType.CURRENCY_COIN,
      gift1Amount: String(row.gift1Amount ?? 0),
      gift2Type: (row.gift2Type as LevelRewardInputType) || LevelRewardInputType.CURRENCY_DIAMOND,
      gift2Amount: String(row.gift2Amount ?? 0),
      betaVersionsIos: buildBetaMapFromNumbers(row.betaIosVersions),
      betaVersionsAndroid: buildBetaMapFromNumbers(row.betaAndroidVersions),
    });
  };
  const handleEditSave = () => {
    if (!editRow) {
      return;
    }
    const eligibleIosVersions = toNumericVersionList(editState.eligibleIosVersionsText);
    const eligibleAndroidVersions = toNumericVersionList(editState.eligibleAndroidVersionsText);
    const betaIosVersions = toNumericVersionListFromMap(
      editState.eligibleIosVersionsText,
      editState.betaVersionsIos
    );
    const betaAndroidVersions = toNumericVersionListFromMap(
      editState.eligibleAndroidVersionsText,
      editState.betaVersionsAndroid
    );
    const betaIosPayload = betaIosVersions.length ? betaIosVersions : null;
    const betaAndroidPayload = betaAndroidVersions.length ? betaAndroidVersions : null;
    if (!eligibleIosVersions.length && !eligibleAndroidVersions.length) {
      alert('Please, enter at least one eligible version');
      return;
    }
    updateGiftHistory({
      variables: {
        id: editRow.id,
        eligibleIosVersions,
        eligibleAndroidVersions,
        betaIosVersions: betaIosPayload,
        betaAndroidVersions: betaAndroidPayload,
        gift1Type: editState.gift1Type,
        gift1Amount: Number(editState.gift1Amount || 0),
        gift2Type: editState.gift2Type,
        gift2Amount: Number(editState.gift2Amount || 0),
      },
    })
      .then(() => {
        setEditRow(null);
        refetchHistory();
      })
      .catch(() => {
        alert('Something went wrong :(');
      });
  };
  const handleDelete = (row: UpdateGiftHistoryRow) => {
    if (!window.confirm('Delete this update gift history entry?')) {
      return;
    }
    deleteGiftHistory({ variables: { id: row.id } })
      .then(() => refetchHistory())
      .catch(() => {
        alert('Something went wrong :(');
      });
  };

  const latestIosVersions = parseVersionList(latestGift.eligibleIosVersionsText);
  const latestAndroidVersions = parseVersionList(latestGift.eligibleAndroidVersionsText);
  const allLatestIosBeta =
    latestIosVersions.length > 0 && latestIosVersions.every((version) => latestGift.betaVersionsIos[version]);
  const allLatestAndroidBeta =
    latestAndroidVersions.length > 0 &&
    latestAndroidVersions.every((version) => latestGift.betaVersionsAndroid[version]);

  return (
    <div className="d-flex flex-column">
      <Modal show={!!editRow} onHide={() => setEditRow(null)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Edit Client Update Gift</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {editRow && (
            <Row className="mt-2">
              <Col xs={3}>Gift ID</Col>
              <Col xs={6}>
                <span className="text-monospace">{editRow.id}</span>
              </Col>
            </Row>
          )}
          <Row className="mt-2">
            <Col xs={3}>
              <span>iOS Versions</span>
              <OverlayTrigger
                placement="right"
                overlay={<Tooltip id="edit-ios-format-tooltip">Use version format: 003.013.000 (comma-separated)</Tooltip>}
              >
                <i className="fas fa-question-circle ml-2 text-muted" />
              </OverlayTrigger>
            </Col>
            <Col xs={6}>
              <Form.Control
                type="text"
                value={editState.eligibleIosVersionsText}
                onChange={(event) => {
                  const value = event.currentTarget?.value ?? '';
                  setEditState((prev) => ({ ...prev, eligibleIosVersionsText: value }));
                }}
                onBlur={(event: React.FocusEvent<HTMLInputElement>) => {
                  const value = event.currentTarget?.value ?? '';
                  const entries = parseVersionEntries(value);
                  setEditState((prev) => ({
                    ...prev,
                    eligibleIosVersionsText: parseVersionList(value).join(', '),
                    betaVersionsIos: mergeBetaMapFromEntries(prev.betaVersionsIos, entries),
                  }));
                }}
              />
            </Col>
          </Row>
          {parseVersionList(editState.eligibleIosVersionsText).length ? (
            <Row className="mt-2">
              <Col xs={3}>Beta iOS</Col>
              <Col xs={8}>
                <div className="d-flex flex-wrap">
                  {parseVersionList(editState.eligibleIosVersionsText).map((version) => (
                    <Form.Check
                      key={`ios-${version}`}
                      type="checkbox"
                      className="mr-3"
                      label={version}
                      checked={Boolean(editState.betaVersionsIos[version])}
                      onChange={(event) => {
                        const checked = event?.currentTarget?.checked ?? false;
                        setEditState((prev) => ({
                          ...prev,
                          betaVersionsIos: { ...prev.betaVersionsIos, [version]: checked },
                        }));
                      }}
                    />
                  ))}
                </div>
              </Col>
            </Row>
          ) : null}
          <Row className="mt-2">
            <Col xs={3}>
              <span>Android Versions</span>
              <OverlayTrigger
                placement="right"
                overlay={<Tooltip id="edit-android-format-tooltip">Use version format: 003.013.000 (comma-separated)</Tooltip>}
              >
                <i className="fas fa-question-circle ml-2 text-muted" />
              </OverlayTrigger>
            </Col>
            <Col xs={6}>
              <Form.Control
                type="text"
                value={editState.eligibleAndroidVersionsText}
                onChange={(event) => {
                  const value = event.currentTarget?.value ?? '';
                  setEditState((prev) => ({ ...prev, eligibleAndroidVersionsText: value }));
                }}
                onBlur={(event: React.FocusEvent<HTMLInputElement>) => {
                  const value = event.currentTarget?.value ?? '';
                  const entries = parseVersionEntries(value);
                  setEditState((prev) => ({
                    ...prev,
                    eligibleAndroidVersionsText: parseVersionList(value).join(', '),
                    betaVersionsAndroid: mergeBetaMapFromEntries(prev.betaVersionsAndroid, entries),
                  }));
                }}
              />
            </Col>
          </Row>
          {parseVersionList(editState.eligibleAndroidVersionsText).length ? (
            <Row className="mt-2">
              <Col xs={3}>Beta Android</Col>
              <Col xs={8}>
                <div className="d-flex flex-wrap">
                  {parseVersionList(editState.eligibleAndroidVersionsText).map((version) => (
                      <Form.Check
                        key={`android-${version}`}
                        type="checkbox"
                        className="mr-3"
                        label={version}
                        checked={Boolean(editState.betaVersionsAndroid[version])}
                        onChange={(event) => {
                          const checked = event?.currentTarget?.checked ?? false;
                          setEditState((prev) => ({
                            ...prev,
                            betaVersionsAndroid: { ...prev.betaVersionsAndroid, [version]: checked },
                          }));
                        }}
                      />
                  ))}
                </div>
              </Col>
            </Row>
          ) : null}
          <Row className="mt-2">
            <Col xs={3}>Gift 1 Type</Col>
            <Col xs={6}>
              <Form.Control
                as="select"
                value={editState.gift1Type}
                onChange={(event) =>
                  setEditState((prev) => ({ ...prev, gift1Type: event.target.value as LevelRewardInputType }))
                }
              >
                {currencyRewards.map((rewardType) => (
                  <option value={rewardType} key={rewardType}>
                    {formatGiftType(rewardType)}
                  </option>
                ))}
              </Form.Control>
            </Col>
          </Row>
          <Row className="mt-2">
            <Col xs={3}>Gift 1 Amount</Col>
            <Col xs={3}>
              <Form.Control
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                value={editState.gift1Amount}
                onChange={(event) => {
                  const value = event.currentTarget?.value ?? '';
                  const sanitized = value.replace(/[^0-9]/g, '');
                  setEditState((prev) => ({ ...prev, gift1Amount: sanitized }));
                }}
              />
            </Col>
            <Col xs={6}>
              <div className="d-flex flex-wrap">
                {Object.entries(rewardPresets).map(([key, preset]) => (
                  <Form.Check
                    key={`edit-preset-${key}`}
                    type="checkbox"
                    className="mr-3"
                    label={`${preset.label} (${preset.coin} coin / ${preset.diamond} diamond)`}
                    checked={editPreset === key}
                    onChange={(event) => {
                      if (event.currentTarget.checked) {
                        applyPresetToEditGift(key as RewardPresetKey);
                      }
                    }}
                  />
                ))}
              </div>
            </Col>
          </Row>
          <Row className="mt-2">
            <Col xs={3}>Gift 2 Type</Col>
            <Col xs={6}>
              <Form.Control
                as="select"
                value={editState.gift2Type}
                onChange={(event) =>
                  setEditState((prev) => ({ ...prev, gift2Type: event.target.value as LevelRewardInputType }))
                }
              >
                {currencyRewards.map((rewardType) => (
                  <option value={rewardType} key={rewardType}>
                    {formatGiftType(rewardType)}
                  </option>
                ))}
              </Form.Control>
            </Col>
          </Row>
          <Row className="mt-2">
            <Col xs={3}>Gift 2 Amount</Col>
            <Col xs={6}>
              <Form.Control
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                value={editState.gift2Amount}
                onChange={(event) => {
                  const value = event.currentTarget?.value ?? '';
                  const sanitized = value.replace(/[^0-9]/g, '');
                  setEditState((prev) => ({ ...prev, gift2Amount: sanitized }));
                }}
              />
            </Col>
          </Row>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setEditRow(null)}>
            Cancel
          </Button>
          <Button disabled={editLoading} onClick={handleEditSave}>
            Save
          </Button>
        </Modal.Footer>
      </Modal>
      <Card className="mb-4">
        <Card.Header className="d-flex align-items-center">
          <span className="font-weight-bold">
            <i className="fas fa-gift mr-2" />
            Set Update Gift Version
          </span>
        </Card.Header>
        <Card.Body>
          <Row className="mt-2">
            <Col xs={3}>
              <span>iOS Versions</span>
              <OverlayTrigger
                placement="right"
                overlay={
                  <Tooltip id="set-ios-format-tooltip">Use version format: 003.013.000 (comma-separated)</Tooltip>
                }
              >
                <i className="fas fa-question-circle ml-2 text-muted" />
              </OverlayTrigger>
            </Col>
            <Col xs={6}>
              <Form.Control
                type="text"
                value={latestGift.eligibleIosVersionsText}
                onChange={(event) => {
                  const value = event.target.value;
                  handleLatestGiftChange({
                    eligibleIosVersionsText: value,
                    eligibleAndroidVersionsText: sameVersion ? value : latestGift.eligibleAndroidVersionsText,
                  });
                }}
                onBlur={(event: React.FocusEvent<HTMLInputElement>) => {
                  const normalized = parseVersionList(event.target.value).join(', ');
                  const entries = parseVersionEntries(event.target.value);
                  handleLatestGiftChange({
                    eligibleIosVersionsText: normalized,
                    eligibleAndroidVersionsText: sameVersion ? normalized : latestGift.eligibleAndroidVersionsText,
                    betaVersionsIos: mergeBetaMapFromEntries(latestGift.betaVersionsIos, entries),
                    betaVersionsAndroid: sameVersion
                      ? mergeBetaMapFromEntries(latestGift.betaVersionsIos, entries)
                      : latestGift.betaVersionsAndroid,
                  });
                }}
              />
            </Col>
          </Row>
          {parseVersionList(latestGift.eligibleIosVersionsText).length ? (
            <Row className="mt-2">
              <Col xs={3}>Beta iOS</Col>
              <Col xs={8}>
                <div className="d-flex flex-wrap">
                  {parseVersionList(latestGift.eligibleIosVersionsText).map((version) => (
                    <Form.Check
                      key={`latest-ios-${version}`}
                      type="checkbox"
                      className="mr-3"
                      label={version}
                      checked={Boolean(latestGift.betaVersionsIos[version])}
                      onChange={(event) => {
                        const checked = event?.currentTarget?.checked ?? false;
                        handleLatestGiftChange({
                          betaVersionsIos: { ...latestGift.betaVersionsIos, [version]: checked },
                        });
                      }}
                    />
                  ))}
                </div>
              </Col>
            </Row>
          ) : null}
          <Row className="mt-2">
            <Col xs={3} />
            <Col xs={6}>
              <Form.Check
                type="checkbox"
                label="Android versions same as iOS"
                checked={sameVersion}
                onChange={(event) => {
                  const checked = event.target.checked;
                  setSameVersion(checked);
                  if (checked) {
                    handleLatestGiftChange({
                      eligibleAndroidVersionsText: latestGift.eligibleIosVersionsText,
                      betaVersionsAndroid: { ...latestGift.betaVersionsIos },
                    });
                  }
                }}
              />
            </Col>
          </Row>
          <Row className="mt-2">
            <Col xs={3}>
              <span>Android Versions</span>
              <OverlayTrigger
                placement="right"
                overlay={
                  <Tooltip id="set-android-format-tooltip">Use version format: 003.013.000 (comma-separated)</Tooltip>
                }
              >
                <i className="fas fa-question-circle ml-2 text-muted" />
              </OverlayTrigger>
            </Col>
            <Col xs={6}>
              <Form.Control
                type="text"
                value={latestGift.eligibleAndroidVersionsText}
                onChange={(event) => handleLatestGiftChange({ eligibleAndroidVersionsText: event.target.value })}
                onBlur={(event: React.FocusEvent<HTMLInputElement>) => {
                  const normalized = parseVersionList(event.target.value).join(', ');
                  const entries = parseVersionEntries(event.target.value);
                  handleLatestGiftChange({
                    eligibleAndroidVersionsText: normalized,
                    betaVersionsAndroid: mergeBetaMapFromEntries(latestGift.betaVersionsAndroid, entries),
                  });
                }}
                disabled={sameVersion}
              />
            </Col>
          </Row>
          {parseVersionList(latestGift.eligibleAndroidVersionsText).length ? (
            <Row className="mt-2">
              <Col xs={3}>Beta Android</Col>
              <Col xs={8}>
                <div className="d-flex flex-wrap">
                  {parseVersionList(latestGift.eligibleAndroidVersionsText).map((version) => (
                    <Form.Check
                      key={`latest-android-${version}`}
                      type="checkbox"
                      className="mr-3"
                      label={version}
                      checked={Boolean(latestGift.betaVersionsAndroid[version])}
                      onChange={(event) => {
                        const checked = event?.currentTarget?.checked ?? false;
                        handleLatestGiftChange({
                          betaVersionsAndroid: { ...latestGift.betaVersionsAndroid, [version]: checked },
                        });
                      }}
                    />
                  ))}
                </div>
              </Col>
            </Row>
          ) : null}
          <Row className="mt-2">
            <Col xs={3}>Gift 1 Type</Col>
            <Col xs={6}>
              <Form.Control
                as="select"
                value={latestGift.gift1Type}
                onChange={(event) => handleLatestGiftChange({ gift1Type: event.target.value })}
              >
                {currencyRewards.map((rewardType) => (
                  <option value={rewardType} key={rewardType}>
                    {formatGiftType(rewardType)}
                  </option>
                ))}
              </Form.Control>
            </Col>
          </Row>
          <Row className="mt-2">
            <Col xs={3}>Gift 1 Amount</Col>
            <Col xs={3}>
              <Form.Control
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                value={latestGift.gift1Amount}
                onChange={(event) => {
                  const value = event.target.value ?? '';
                  const sanitized = value.replace(/[^0-9]/g, '');
                  handleLatestGiftChange({ gift1Amount: sanitized });
                }}
              />
            </Col>
            <Col xs={6}>
              <div className="d-flex flex-wrap">
                {Object.entries(rewardPresets).map(([key, preset]) => (
                  <Form.Check
                    key={`latest-preset-${key}`}
                    type="checkbox"
                    className="mr-3"
                    label={`${preset.label} (${preset.coin} coin / ${preset.diamond} diamond)`}
                    checked={latestPreset === key}
                    onChange={(event) => {
                      if (event.currentTarget.checked) {
                        applyPresetToLatestGift(key as RewardPresetKey);
                      }
                    }}
                  />
                ))}
              </div>
            </Col>
          </Row>
          <Row className="mt-2">
            <Col xs={3}>Gift 2 Type</Col>
            <Col xs={6}>
              <Form.Control
                as="select"
                value={latestGift.gift2Type}
                onChange={(event) => handleLatestGiftChange({ gift2Type: event.target.value })}
              >
                {currencyRewards.map((rewardType) => (
                  <option value={rewardType} key={rewardType}>
                    {formatGiftType(rewardType)}
                  </option>
                ))}
              </Form.Control>
            </Col>
          </Row>
          <Row className="mt-2">
            <Col xs={3}>Gift 2 Amount</Col>
            <Col xs={6}>
              <Form.Control
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                value={latestGift.gift2Amount}
                onChange={(event) => {
                  const value = event.target.value ?? '';
                  const sanitized = value.replace(/[^0-9]/g, '');
                  handleLatestGiftChange({ gift2Amount: sanitized });
                }}
              />
            </Col>
          </Row>
          <div className="mt-3 d-flex justify-content-end">
            <Button disabled={saveLoading} onClick={handleSave}>
              Save Update Gift
            </Button>
          </div>
        </Card.Body>
      </Card>

      <Card className="update-gift-history-card">
        <Card.Header className="d-flex align-items-center">
          <span className="font-weight-bold">
            <i className="fas fa-history mr-2" />
            Client Update Gift
          </span>
          {historyLoading && <Spinner animation="border" size="sm" className="ml-2" />}
        </Card.Header>
        <Card.Body>
          <div className="alert py-2 mb-2 text-center update-gift-history-note" role="alert">
            <div>
              - When you enter a version, you will see a BETA iOS/Android option appear. Please check the box if the
              version you input is a BETA build.
            </div>
            <div className="mt-1">
              - If a BETA release uses version 003.300.013 and all of the same features are later released to PROD
              under a different version (e.g., 003.300.021), you do not need to create a new Gift with a different
              Gift ID. Instead, edit the existing Gift ID and add the PROD version to the version list. This ensures
              that BETA players do not receive duplicate rewards.
            </div>
          </div>
          <Table bordered size="sm" className="mb-0 update-gift-history-table">
            <thead>
              <tr>
                <th>Created At</th>
                <th>Gift ID</th>
                <th>iOS Versions</th>
                <th>Android Versions</th>
                <th>Gift 1 Type</th>
                <th>Gift 1 Amount</th>
                <th>Gift 2 Type</th>
                <th>Gift 2 Amount</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {historyRows.length ? (
                historyRows.map((row, index) => (
                  <tr
                    key={row.id}
                    className={`update-gift-history-row ${index % 2 === 0 ? 'is-base' : 'is-alt'}`}
                  >
                    <td>{new Date(Number(row.createdAt)).toLocaleString('en-GB')}</td>
                    <td>
                      <span className="text-monospace">{row.id}</span>
                    </td>
                    <td style={{ whiteSpace: 'pre-line' }}>
                      {formatVersionListMultiline(
                        row.eligibleIosVersions,
                        false,
                        buildBetaMapFromNumbers(row.betaIosVersions)
                      )}
                    </td>
                    <td>
                      <span style={{ whiteSpace: 'pre-line' }}>
                        {formatVersionListMultiline(
                          row.eligibleAndroidVersions,
                          false,
                          buildBetaMapFromNumbers(row.betaAndroidVersions)
                        )}
                      </span>
                    </td>
                    <td>{formatGiftType(row.gift1Type)}</td>
                    <td>{row.gift1Amount}</td>
                    <td>{formatGiftType(row.gift2Type)}</td>
                    <td>{row.gift2Amount}</td>
                    <td className="text-nowrap">
                      <div className="d-inline-flex align-items-center">
                        <Button variant="secondary" size="sm" onClick={() => handleEditOpen(row)}>
                          Edit
                        </Button>
                      </div>
                      <Button
                        variant="danger"
                        size="sm"
                        className="ml-2"
                        disabled={deleteLoading}
                        onClick={() => handleDelete(row)}
                      >
                        Delete
                      </Button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={9} className="text-center text-muted">
                    No update gift history yet.
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </Card.Body>
      </Card>
    </div>
  );
};

export default UpdateGiftHistory;
